import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowRight, ArrowRightLeft, CalendarDays, Check, CheckCircle2, ChevronDown, Building2, ClipboardCheck, Copy, Download, Eye, Maximize2, Minimize2, FileCode2, FileText, Lock, PackageCheck, Plus, Save, Search, ShoppingCart, Trash2, Truck, Undo2, Wand2 } from 'lucide-react';
import { api, ErroHttp } from '@/lib/api';
import { caixaAlta, cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { acaoTexto, Botao } from '@/componentes/ui/botao';
import { AuxiliaresNfCompra } from './AuxiliaresNfCompra';
import { Campo, CampoComIcone, Cartao, Esqueleto } from '@/componentes/ui/primitivos';

/**
 * Aba "NF de compra": consultar e acertar a NF_COMPRA pelo WMS, em vez do
 * formulário do Procfit (pedido do Paulo, 30/10/2026, sobre a lista de acertos
 * da Geisy). Lista à esquerda com scroll próprio, editor fixo à direita.
 *
 * Salvar grava só o que mudou e salva o formulário como o Procfit (Validação e
 * Gravação Final do dicionário). Validada: só os campos fiscais.
 */

interface OrigemTransferencia {
  empresaOrigem: number | null;
  peloWms: boolean;
  emitidaPor: string | null;
  compras: { numero: number; nfCompra: number | null }[];
}

interface DestinoTransferencia {
  nfCompraDestino: number;
  numero: number | null;
  empresa: number | null;
  destino: string | null;
  peloWms: boolean;
  emitidaPor: string | null;
  recebimento: number | null;
}

interface TrilhaTransferencia {
  compras: {
    numero: number;
    nfCompra: number | null;
    fornecedor: string | null;
    valor: number | null;
    empresa: number | null;
    emissao: string | null;
    lancadaEm: string | null;
    agenda: { data: string | null; janela: string | null; destino: string | null } | null;
    provavel: boolean;
  }[];
  /** Produtos cuja quantidade difere entre a transferência e as compras (vazio = batem; null = sem compras). */
  quantidades?: { produto: number; transferida: number; compras: number }[] | null;
  saida: { nfFaturamento: number; numero: number; serie: string | null; empresa: number; emissao: string | null; total: number | null; operacao: number | null; cancelada: boolean } | null;
  entrada: { nfCompra: number; numero: number | null; empresa: number; emissao: string | null; total: number | null; validada: boolean; lancadaEm: string | null };
  cega: { recebimento: number; aprovada: boolean; alocada: boolean; contada: boolean; criadaEm: string | null } | null;
  peloWms: boolean;
  emitidaPor: string | null;
}

interface LinhaNf {
  nfCompra: number;
  numero: number | null;
  serie: string | null;
  empresa: number;
  fornecedor: string | null;
  emissao: string | null;
  movimento: string | null;
  pedidoCompra: number | null;
  total: number | null;
  validada: boolean;
  parcelas: number;
  operacoes: string | null;
  /** A operação da maioria dos itens, com o nome ("4 · COMPRA PARA COMERCIALIZAÇÃO"). */
  operacao: string | null;
  tipo: TipoNf;
  /** Na busca: por que a NF veio ("SKU 31579", "OC 43508"…). */
  achadaPor?: string | null;
  etapa: string | null;
  concluida: boolean;
  recebimento: number | null;
  transferencia: OrigemTransferencia | null;
  transferida: DestinoTransferencia | null;
  pendencias: { tipo: 'erro' | 'atencao' | 'info'; texto: string }[];
}

type TipoNf = 'compra' | 'venda_ordem' | 'remessa' | 'transferencia' | 'consumo' | 'servico' | 'outras';

/** O filtro da lista pelo tipo da operação principal (a classificação é da API, pelo nome da operação). */
const TIPOS_NF: { tipo: TipoNf; rotulo: string; dica: string }[] = [
  { tipo: 'compra', rotulo: 'Compras', dica: 'Compra para comercialização (4, 265…), importação e consignação' },
  { tipo: 'venda_ordem', rotulo: 'Venda à ordem', dica: 'A compra faturada pelo fornecedor na venda à ordem (266, 277, 76…)' },
  { tipo: 'remessa', rotulo: 'Remessas', dica: 'A remessa da mercadoria da venda à ordem (267, 278, 60…): sem OC e sem parcela é o normal' },
  { tipo: 'transferencia', rotulo: 'Transferências', dica: 'Entrada de transferência entre empresas do grupo (42, 273…)' },
  { tipo: 'consumo', rotulo: 'Uso e consumo', dica: 'Material de uso e consumo, embalagem, energia, comunicação' },
  { tipo: 'servico', rotulo: 'Serviço/frete', dica: 'NF sem produto, só serviço (o frete das transportadoras)' },
  { tipo: 'outras', rotulo: 'Outras', dica: 'Devolução, doação/brinde, consignação, conserto…' },
];

const TOM_PENDENCIA = {
  erro: 'bg-status-divergenciaFundo text-status-divergencia',
  atencao: 'bg-status-atencaoFundo text-status-atencao',
  info: 'bg-superficie2 text-textoFraco',
} as const;

interface Item {
  id: number;
  produto: number;
  descricao: string | null;
  quantidade: number;
  total: number | null;
  operacao: number | null;
  operacaoDescricao: string | null;
  cst: string | null;
  icmsAliquota: number | null;
  icmsCredito: string | null;
  pisAliquota: number | null;
  pisValor: number | null;
  cofinsAliquota: number | null;
  cofinsValor: number | null;
  centro: number | null;
}

interface Parcela {
  id: number;
  titulo: string | null;
  vencimento: string | null;
  dias: number | null;
  valor: number;
  classificacao: number | null;
  formaPagamento: string | null;
}

interface Detalhe {
  nfCompra: number;
  numero: number | null;
  serie: string | null;
  empresa: number;
  fornecedor: string | null;
  emissao: string | null;
  movimento: string | null;
  pedidoCompra: number | null;
  totalGeral: number | null;
  totalPis: number | null;
  totalCofins: number | null;
  validada: boolean;
  titulos: number;
  tituloPago: boolean;
  chaveEsteira: string | null;
  chave: string | null;
  /** As empresas que o usuário enxerga, com o nome mais curto do cadastro. */
  empresas?: { empresa: number; nome: string; nomeCompleto: string }[];
  transferencia: OrigemTransferencia | null;
  transferida: DestinoTransferencia | null;
  remessa: boolean;
  trilha: TrilhaTransferencia | null;
  recebimento: number | null;
  travas: { fiscal: string | null; financeiro: string | null };
  itens: Item[];
  parcelas: Parcela[];
  podeEditar: boolean;
}

const reais = (v: number | null): string => (v == null ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const dia = (d: string | null): string => (d ? d.split('-').reverse().join('/') : '—');

export function NfCompra(): JSX.Element {
  const [params, definirParams] = useSearchParams();
  const [busca, definirBusca] = React.useState('');
  const [soPendentes, definirSoPendentes] = React.useState(false);
  const [tipo, definirTipo] = React.useState<TipoNf | null>(null);
  const buscaAtrasada = useDebounce(busca, 350);
  const aberta = Number(params.get('nf')) || null;
  // Expandir: o editor toma o espaço da lista; "Voltar à lista" devolve. Lembrado no navegador.
  const [expandido, definirExpandido] = React.useState<boolean>(() => {
    try {
      return window.localStorage.getItem(CHAVE_EXPANDIDO) === '1';
    } catch {
      return false;
    }
  });
  const alternarExpandido = (): void =>
    definirExpandido((x) => {
      try {
        window.localStorage.setItem(CHAVE_EXPANDIDO, x ? '0' : '1');
      } catch {
        /* sem armazenamento: vale só nesta tela */
      }
      return !x;
    });
  const cheio = expandido && aberta != null;
  const abrir = (nf: number | null): void => {
    const novo = new URLSearchParams(params);
    if (nf) novo.set('nf', String(nf));
    else novo.delete('nf');
    definirParams(novo, { replace: true });
  };
  const lista = useQuery<{ notas: LinhaNf[]; tipos?: Record<TipoNf, number> }>({
    queryKey: ['nf-compra-lista', buscaAtrasada, soPendentes, tipo],
    queryFn: () => api.get('/api/entradas/nf-compra', { busca: buscaAtrasada, pendentes: soPendentes ? '1' : '', tipo: tipo ?? '' }),
    placeholderData: (anterior) => anterior,
  });
  const contagem = lista.data?.tipos;
  const totalTipos = contagem ? Object.values(contagem).reduce((a, b) => a + b, 0) : null;
  const alturaLista = useAlturaAteORodape();

  return (
    <div className={cn('grid items-start gap-3', !cheio && 'lg:grid-cols-[minmax(320px,420px)_minmax(0,1fr)]')}>
      <div ref={alturaLista.ref} style={alturaLista.estilo} className={cn('lg:sticky lg:top-3 lg:min-h-[360px]', cheio && 'hidden')}>
        <Cartao className="flex h-full flex-col">
          <div className="border-b border-borda p-3">
            <CampoComIcone
              icone={<Search className="h-4 w-4" />}
              value={busca}
              onChange={(e) => definirBusca(e.target.value)}
              placeholder="NF, nº da entrada, OC, SKU ou fornecedor"
              aria-label="Buscar NF de compra"
            />
            <div className="mt-1.5 flex items-center justify-between gap-2 text-2xs text-textoSuave">
              <span>Sem busca: as dos últimos 60 dias, mais novas primeiro.</span>
              <label className="flex shrink-0 cursor-pointer items-center gap-1 font-semibold text-textoFraco">
                <input type="checkbox" checked={soPendentes} onChange={(e) => definirSoPendentes(e.target.checked)} />
                Só com pendência
              </label>
            </div>
            {/* Tipo pela operação da NF; o número é do mesmo recorte (busca ou 60 dias). Tipo vazio some. */}
            <div className="mt-2 flex flex-wrap gap-1 text-2xs">
              {[{ tipo: null, rotulo: 'Todas', dica: 'Todos os tipos' }, ...TIPOS_NF].map((t) => {
                const n = t.tipo ? contagem?.[t.tipo] : totalTipos;
                if (t.tipo && contagem && !n && tipo !== t.tipo) return null;
                const ativo = tipo === t.tipo;
                return (
                  <button
                    key={t.tipo ?? 'todas'}
                    type="button"
                    aria-pressed={ativo}
                    title={t.dica}
                    onClick={() => definirTipo(t.tipo)}
                    className={cn(
                      'rounded-full border px-2 py-0.5 font-semibold transition',
                      ativo ? 'border-primaria bg-primaria text-white' : 'border-borda bg-superficie text-textoFraco hover:border-primaria/50 hover:text-primaria',
                    )}
                  >
                    {t.rotulo}
                    {n != null ? <span className={cn('ml-1 tabular-nums', ativo ? 'text-white/80' : 'text-textoSuave')}>{n}</span> : null}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {lista.isLoading ? (
              <div className="space-y-2 p-3">
                <Esqueleto className="h-12" />
                <Esqueleto className="h-12" />
              </div>
            ) : (
              (lista.data?.notas ?? []).map((n) => (
                <button
                  key={n.nfCompra}
                  type="button"
                  onClick={() => abrir(n.nfCompra)}
                  className={cn(
                    'block w-full border-b border-borda px-3 py-2 text-left text-xs transition hover:bg-superficie2',
                    // Recebida e toda alocada: o card inteiro em verde claro.
                    n.concluida && 'bg-status-livreFundo hover:bg-status-livreFundo/80',
                    aberta === n.nfCompra && (n.concluida ? 'ring-1 ring-inset ring-status-livre' : 'bg-primaria-suave/60'),
                  )}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="truncate font-semibold text-texto">{caixaAlta(n.fornecedor) ?? 'sem fornecedor'}</span>
                    <span className="flex shrink-0 items-center gap-1.5">
                      {n.achadaPor ? (
                        <span className="rounded bg-superficie2 px-1 text-2xs font-semibold text-textoFraco" title="Por que esta NF apareceu na busca">
                          {n.achadaPor}
                        </span>
                      ) : null}
                      <span className="tabular text-textoFraco">{reais(n.total)}</span>
                    </span>
                  </span>
                  <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-textoFraco">
                    <span className="tabular">NF {n.numero}</span>
                    <span className="tabular">· {n.nfCompra}</span>
                    {n.pedidoCompra ? <span className="tabular">· OC {n.pedidoCompra}</span> : null}
                    <span>· mov. {dia(n.movimento)}</span>
                    {n.etapa ? <span>· {n.etapa}</span> : null}
                  </span>
                  {/* A operação da NF, discreta: a principal com o nome; as outras, se houver, só o número. */}
                  {n.operacao || n.operacoes ? (
                    <span
                      className="mt-0.5 block truncate text-2xs text-textoSuave"
                      title={`Operações dos itens: ${n.operacoes ?? '—'}${n.operacao ? `\nPrincipal: ${n.operacao}` : ''}`}
                    >
                      op {n.operacao ?? n.operacoes}
                      {n.operacao && n.operacoes && n.operacoes.includes(',') ? ` (+ ${n.operacoes.split(', ').filter((o) => o !== n.operacao!.split(' · ')[0]).join(', ')})` : ''}
                    </span>
                  ) : null}
                  <span className="mt-1 flex flex-wrap items-center gap-1">
                    {n.concluida ? (
                      <span className="inline-flex items-center gap-0.5 rounded bg-status-livre px-1 text-2xs font-semibold text-white">
                        <CheckCircle2 className="h-3 w-3" /> Concluída{n.recebimento ? ` · cega ${n.recebimento} alocada` : ''}
                      </span>
                    ) : null}
                    {n.transferencia ? (
                      <span
                        className="inline-flex items-center gap-0.5 rounded bg-primaria-suave px-1 text-2xs font-semibold text-primaria"
                        title={n.transferencia.compras.length ? `Compras de origem: NF ${n.transferencia.compras.map((x) => x.numero).join(', ')}` : undefined}
                      >
                        <ArrowRightLeft className="h-3 w-3" /> Transferência{n.transferencia.empresaOrigem ? ` da empresa ${n.transferencia.empresaOrigem}` : ''} ·{' '}
                        {n.transferencia.peloWms ? 'pelo WMS' : 'pelo Procfit'}
                      </span>
                    ) : null}
                    {n.transferida ? (
                      <span
                        className="inline-flex items-center gap-0.5 rounded bg-primaria-suave px-1 text-2xs font-semibold text-primaria"
                        title={`NF de transferência ${n.transferida.numero ?? '?'} · entrada ${n.transferida.nfCompraDestino}`}
                      >
                        <ArrowRightLeft className="h-3 w-3" /> Transferida → {n.transferida.destino ?? `empresa ${n.transferida.empresa ?? '?'}`}
                      </span>
                    ) : null}
                    {n.concluida ? null : n.validada && !n.transferencia ? (
                      <span className="rounded bg-status-livreFundo px-1 text-2xs font-semibold text-status-livre">validada</span>
                    ) : null}
                    {n.pendencias.map((p) => (
                      <span key={p.texto} className={cn('rounded px-1 text-2xs font-semibold', TOM_PENDENCIA[p.tipo])}>
                        {p.texto}
                      </span>
                    ))}
                  </span>
                </button>
              ))
            )}
            {lista.data && !lista.data.notas.length ? <p className="p-4 text-xs text-textoSuave">Nenhuma NF de compra.</p> : null}
          </div>
        </Cartao>
      </div>
      {aberta ? (
        <EditorNf key={aberta} nfCompra={aberta} expandido={cheio} aoExpandir={alternarExpandido} />
      ) : (
        <Cartao className="p-8 text-center text-sm text-textoSuave">Escolha uma NF de compra à esquerda.</Cartao>
      )}
    </div>
  );
}

type EdItem = { operacao?: string; cst?: string; icmsCredito?: string; pisAliquota?: string; cofinsAliquota?: string; centro?: string };
type EdParcela = { vencimento?: string; valor?: string; classificacao?: string; formaPagamento?: string };

/**
 * O caminho da transferência numa linha do tempo discreta (pedido do Paulo,
 * 30/10/2026): uma linha por etapa, com a data e o que aquela NF É — a compra
 * do fornecedor, a saída da origem e a entrada no destino são três registros,
 * mas a saída e a entrada são o MESMO documento fiscal visto de cada lado.
 * A NF aberta fica marcada com "esta".
 */
const CHAVE_TRILHA = 'wms.nfcompra.trilha.aberta';
const CHAVE_EXPANDIDO = 'wms.nfcompra.editor.expandido';

function Trilha({ t, atual }: { t: TrilhaTransferencia; atual: number }): JSX.Element {
  const [aberto, definirAberto] = React.useState<boolean>(() => {
    try {
      return window.localStorage.getItem(CHAVE_TRILHA) === '1';
    } catch {
      return false;
    }
  });
  const alternar = (): void =>
    definirAberto((x) => {
      try {
        window.localStorage.setItem(CHAVE_TRILHA, x ? '0' : '1');
      } catch {
        /* sem armazenamento: só não lembra */
      }
      return !x;
    });
  const somaCompras = t.compras.reduce((a, c) => a + (c.valor ?? 0), 0);
  const valorSaida = t.saida?.total ?? t.entrada.total ?? null;
  const valorDifere = t.compras.length > 0 && valorSaida != null && Math.abs(somaCompras - valorSaida) > 0.05;
  // Quantidade por produto é o que diz se é parcial; o valor sozinho pode ser só preço de transferência.
  const qtdDiferentes = t.quantidades ?? null;
  const qtdBatem = qtdDiferentes !== null && qtdDiferentes.length === 0;
  const difere = valorDifere || (qtdDiferentes !== null && qtdDiferentes.length > 0);
  const nCompras = `${t.compras.length} ${t.compras.length === 1 ? 'compra' : 'compras'}`;
  const quando = (iso: string | null): string => (iso ? dia(iso.slice(0, 10)) : '');
  // Cada etapa: o que É (principal), os fatos curtos, a explicação em cinza e as ações à direita.
  type Acao = { rotulo: string; href: string; nova?: boolean };
  type Passo = {
    titulo: string;
    data: string;
    feito: boolean;
    esta?: boolean;
    alerta?: boolean;
    principal: string;
    fatos: string[];
    explica: string;
    acoes: Acao[];
  };
  const abrirNf = (nf: number | null, rotulo = 'Abrir'): Acao[] => (nf && nf !== atual ? [{ rotulo, href: `/entradas?aba=nfcompra&nf=${nf}` }] : []);
  const passos: Passo[] = [];
  for (const c of t.compras) {
    passos.push({
      titulo: 'Compra',
      data: quando(c.emissao),
      esta: c.nfCompra === atual,
      feito: true,
      principal: `NF ${c.numero} · ${caixaAlta(c.fornecedor) ?? 'fornecedor'}`,
      fatos: [`empresa ${c.empresa ?? '?'}`, reais(c.valor), ...(c.provavel ? ['provável'] : [])],
      explica: c.provavel
        ? 'Provável: achada pelo produto e quantidade. A transferência juntou ou dividiu compras, então não dá para afirmar.'
        : 'A compra em si: é ela que gera o título a pagar.',
      acoes: abrirNf(c.nfCompra),
    });
    if (c.agenda) {
      passos.push({
        titulo: 'Agendamento',
        data: quando(c.agenda.data),
        feito: Boolean(c.agenda.data),
        principal: `empresa ${c.empresa ?? '?'} → ${c.agenda.destino ?? 'destino'}`,
        fatos: c.agenda.data ? [`chegada ${dia(c.agenda.data)}${c.agenda.janela ? ` ${c.agenda.janela}` : ''}`] : [],
        explica: c.agenda.data ? 'Quando a carga chega no destino.' : 'Sem data agendada no WMS: a transferência foi feita direto no Procfit.',
        acoes: [],
      });
    }
  }
  if (t.saida) {
    passos.push({
      titulo: 'Saída',
      data: quando(t.saida.emissao),
      feito: !t.saida.cancelada,
      alerta: t.saida.cancelada,
      principal: `NF de transferência ${t.saida.numero}/${t.saida.serie ?? ''}${t.saida.cancelada ? ' · cancelada' : ''}`,
      fatos: [`empresa ${t.saida.empresa}`, reais(t.saida.total), `${t.peloWms ? 'pelo WMS' : 'pelo Procfit'}${t.emitidaPor ? ` · ${t.emitidaPor}` : ''}`],
      explica: 'Tira a mercadoria do estoque da origem.',
      acoes: [{ rotulo: 'DANFE', href: `/api/entradas/nf-compra/${t.entrada.nfCompra}/arquivo/pdf`, nova: true }],
    });
  } else {
    passos.push({
      titulo: 'Saída',
      data: '',
      feito: false,
      principal: 'NF de saída não achada',
      fatos: [],
      explica: 'Procurada no ERP pelo número e série da entrada.',
      acoes: [],
    });
  }
  passos.push({
    titulo: 'Entrada',
    data: quando(t.entrada.lancadaEm),
    esta: t.entrada.nfCompra === atual,
    feito: t.entrada.validada,
    principal: `NF ${t.entrada.numero ?? ''} · NF_COMPRA ${t.entrada.nfCompra}`,
    fatos: [`empresa ${t.entrada.empresa}`, t.entrada.validada ? 'validada' : 'pré-entrada'],
    explica: 'A mesma NF da saída, lançada no destino: põe no estoque de lá quando a cega é alocada.',
    acoes: abrirNf(t.entrada.nfCompra),
  });
  passos.push({
    titulo: 'Recebimento',
    data: quando(t.cega?.criadaEm ?? null),
    feito: Boolean(t.cega?.alocada),
    principal: t.cega ? `Cega ${t.cega.recebimento}` : 'Sem cega ainda',
    fatos: t.cega ? [t.cega.alocada ? 'toda alocada' : t.cega.aprovada ? 'aprovada, alocando' : t.cega.contada ? 'contada, falta aprovar' : 'aguardando contagem'] : [],
    explica: t.cega ? 'A conferência física no destino.' : 'A cega sai quando a carga chega no destino.',
    acoes: t.cega ? [{ rotulo: 'Ver cega', href: `/recebimento?recebimento=${t.cega.recebimento}` }] : [],
  });

  // O passo a passo (recolhido): uma bolinha por etapa; várias compras no mesmo caminhão viram uma etapa só.
  const ETAPAS: { titulo: string; icone: typeof ShoppingCart }[] = [
    { titulo: 'Compra', icone: ShoppingCart },
    { titulo: 'Agendamento', icone: CalendarDays },
    { titulo: 'Saída', icone: Truck },
    { titulo: 'Entrada', icone: PackageCheck },
    { titulo: 'Recebimento', icone: ClipboardCheck },
  ];
  const iconeDe = (titulo: string): typeof ShoppingCart => ETAPAS.find((e) => e.titulo === titulo)?.icone ?? ShoppingCart;
  const etapas = ETAPAS.map((e) => {
    const deste = passos.filter((p) => p.titulo === e.titulo);
    return {
      ...e,
      existe: deste.length > 0,
      feito: deste.length > 0 && deste.every((p) => p.feito),
      alerta: deste.some((p) => p.alerta),
      esta: deste.some((p) => p.esta),
      data: deste.find((p) => p.data)?.data ?? '',
      qtd: deste.length,
    };
  }).filter((e) => e.existe);

  return (
    <div className="rounded-xl border border-borda bg-superficie2/40">
      <div className="flex items-center justify-between gap-2 px-3 pt-2.5">
        <p className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-textoSuave">
          <ArrowRightLeft className="h-3.5 w-3.5 text-primaria" /> Caminho da transferência
        </p>
        <button
          type="button"
          onClick={alternar}
          className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-2xs font-semibold text-texto transition hover:bg-superficie2 hover:text-primaria"
          aria-expanded={aberto}
        >
          {aberto ? 'Recolher' : 'Detalhes'}
          <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', aberto && 'rotate-180')} />
        </button>
      </div>

      {/* Passos ligados por um traço: verde = feito, cinza = falta, vermelho = problema, anel = a NF aberta. */}
      <ol className="flex items-start px-3 pb-3 pt-2">
        {etapas.map((e, k) => {
          const Icone = e.icone;
          const proximaFeita = etapas[k + 1]?.feito;
          return (
            <li key={e.titulo} className="relative flex flex-1 flex-col items-center text-center">
              {k < etapas.length - 1 ? (
                <span
                  className={cn('absolute left-1/2 top-[15px] h-0.5 w-full', e.feito && proximaFeita ? 'bg-status-livre/60' : 'bg-bordaForte')}
                  aria-hidden
                />
              ) : null}
              <span
                className={cn(
                  'relative z-[1] grid h-[30px] w-[30px] place-items-center rounded-full border-2 bg-superficie',
                  e.alerta
                    ? 'border-status-divergencia text-status-divergencia'
                    : e.feito
                      ? 'border-status-livre bg-status-livreFundo text-status-livre'
                      : 'border-bordaForte text-textoFraco',
                  e.esta && 'ring-2 ring-primaria ring-offset-2 ring-offset-superficie',
                )}
                title={e.esta ? 'Esta NF' : undefined}
              >
                <Icone className="h-3.5 w-3.5" />
              </span>
              <span className={cn('mt-1 text-2xs font-semibold', e.esta ? 'text-primaria' : 'text-texto')}>
                {e.titulo}
                {e.qtd > 1 ? ` (${e.qtd})` : ''}
              </span>
              <span className="tabular text-2xs text-textoFraco">{e.data || '—'}</span>
            </li>
          );
        })}
      </ol>

      {aberto ? (
        <ul className="divide-y divide-borda border-t border-borda">
          {passos.map((p, k) => {
            const Icone = iconeDe(p.titulo);
            return (
              <li key={k} className={cn('flex items-start gap-3 px-3 py-2', p.esta && 'bg-primaria-suave/30')}>
                <span
                  className={cn(
                    'mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full',
                    p.alerta ? 'bg-status-divergenciaFundo text-status-divergencia' : p.feito ? 'bg-status-livreFundo text-status-livre' : 'bg-superficie3 text-textoFraco',
                  )}
                >
                  <Icone className="h-3 w-3" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-baseline gap-x-2 text-xs">
                    <span className="font-semibold text-texto">{p.titulo}</span>
                    {p.data ? <span className="tabular text-2xs text-textoFraco">{p.data}</span> : null}
                    {p.esta ? <span className="rounded bg-primaria px-1 text-[10px] font-semibold text-white">esta NF</span> : null}
                  </p>
                  <p className={cn('text-xs', p.alerta ? 'font-semibold text-status-divergencia' : 'text-texto')}>{p.principal}</p>
                  {p.fatos.length ? <p className="text-2xs text-textoFraco">{p.fatos.join(' · ')}</p> : null}
                  <p className="text-2xs text-textoFraco">{p.explica}</p>
                </div>
                {p.acoes.length ? (
                  <div className="flex shrink-0 flex-col items-end gap-1 pt-0.5">
                    {p.acoes.map((a) => (
                      <a
                        key={a.rotulo}
                        href={a.href}
                        {...(a.nova ? { target: '_blank', rel: 'noreferrer' } : {})}
                        className="rounded-md border border-bordaForte bg-superficie2 px-2 py-0.5 text-2xs font-semibold text-texto transition hover:border-primaria hover:text-primaria"
                      >
                        {a.rotulo}
                      </a>
                    ))}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      {difere ? (
        <p className="flex items-center gap-1 border-t border-borda px-3 py-1.5 text-2xs text-status-atencao">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          {qtdBatem ? (
            <span>
              Quantidades batem com as {nCompras}; só o valor difere: compras {reais(somaCompras)}, transferência {reais(valorSaida)} (
              {valorSaida != null && valorSaida > somaCompras ? '+' : '−'}
              {reais(Math.abs((valorSaida ?? 0) - somaCompras))}). Preço de transferência diferente do custo da compra.
            </span>
          ) : qtdDiferentes && qtdDiferentes.length > 0 ? (
            <span>
              Quantidade diferente das {nCompras}:{' '}
              {qtdDiferentes
                .slice(0, 4)
                .map((q) => `SKU ${q.produto} transferiu ${q.transferida}, compras ${q.compras}`)
                .join(' · ')}
              {qtdDiferentes.length > 4 ? ` · e mais ${qtdDiferentes.length - 4}` : ''}
              {qtdDiferentes.every((q) => q.transferida <= q.compras) ? ' — transferência parcial.' : '.'}
            </span>
          ) : (
            <span>
              Compras somam {reais(somaCompras)} e a transferência {reais(valorSaida)}: transferência parcial ou com valor diferente.
            </span>
          )}
        </p>
      ) : null}
    </div>
  );
}

/** A chave da NF-e à vista, em grupos de 4 (como na DANFE), com Copiar. */
function ChaveNfe({ chave }: { chave: string }): JSX.Element {
  const [copiada, definirCopiada] = React.useState(false);
  const copiar = (): void => {
    void navigator.clipboard?.writeText(chave).then(() => {
      definirCopiada(true);
      window.setTimeout(() => definirCopiada(false), 1500);
    });
  };
  // Só o botão (Paulo, 01/11/2026: os 44 dígitos ocupam espaço demais); a chave aparece ao passar o mouse.
  return (
    <button
      type="button"
      onClick={copiar}
      title={`Chave da NF-e: ${chave.replace(/(\d{4})(?=\d)/g, '$1 ')}`}
      className={cn(
        'inline-flex h-7 shrink-0 items-center gap-1 rounded-md border px-2 text-2xs font-semibold transition',
        copiada ? 'border-status-livre/40 bg-status-livreFundo text-status-livre' : 'border-borda bg-superficie text-texto hover:border-primaria hover:text-primaria',
      )}
    >
      {copiada ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copiada ? 'Copiada' : 'Copiar chave'}
    </button>
  );
}

/** Campo que não se edita aqui, no mesmo formato dos editáveis (rótulo em cima, 32px), sem borda de campo. */
function CampoLeitura({ rotulo, valor }: { rotulo: string; valor: string }): JSX.Element {
  return (
    <div title="Não se altera por aqui">
      <span className="block text-[10px] font-bold uppercase leading-4 tracking-wide text-textoSuave">{rotulo}</span>
      <span className="tabular mt-1 flex h-8 items-center justify-between gap-1.5 whitespace-nowrap rounded-lg bg-superficie2 px-2.5 text-[13px] font-semibold text-texto">
        {valor}
        <Lock className="h-3 w-3 shrink-0 text-textoSuave/70" />
      </span>
    </div>
  );
}

function EditorNf({ nfCompra, expandido, aoExpandir }: { nfCompra: number; expandido: boolean; aoExpandir: () => void }): JSX.Element {
  const cliente = useQueryClient();
  const nf = useQuery<Detalhe>({ queryKey: ['nf-compra', nfCompra], queryFn: () => api.get(`/api/entradas/nf-compra/${nfCompra}`) });
  const ops = useQuery<{ operacoes: { id: number; descricao: string }[] }>({
    queryKey: ['operacoes-fiscais'],
    queryFn: () => api.get('/api/entradas/operacoes-fiscais'),
    staleTime: 3_600_000,
  });
  const [oc, definirOc] = React.useState('');
  const [empresa, definirEmpresa] = React.useState('');
  const [movimento, definirMovimento] = React.useState('');
  const [emissao, definirEmissao] = React.useState('');
  const [itens, definirItens] = React.useState<Record<number, EdItem>>({});
  const [parcelas, definirParcelas] = React.useState<Record<number, EdParcela>>({});
  const [novas, definirNovas] = React.useState<{ vencimento: string; valor: string }[]>([]);
  const [excluir, definirExcluir] = React.useState<number[]>([]);
  const [gerarOc, definirGerarOc] = React.useState(false);
  /** O "ignorar validação" mudado na tela (null = como está no ERP). */
  const [ignorar, definirIgnorar] = React.useState<'S' | 'N' | null>(null);
  const limpar = (): void => {
    definirOc('');
    definirEmpresa('');
    definirMovimento('');
    definirEmissao('');
    definirItens({});
    definirParcelas({});
    definirNovas([]);
    definirExcluir([]);
    definirGerarOc(false);
    definirIgnorar(null);
  };
  const salvar = useMutation({
    mutationFn: () =>
      api.put<{ mudancas: string[] }>(`/api/entradas/nf-compra/${nfCompra}`, {
        pedidoCompra: oc ? Number(oc) : null,
        empresa: empresa ? Number(empresa) : null,
        movimento: movimento || null,
        emissao: emissao || null,
        itens: Object.entries(itens).map(([id, e]) => ({
          id: Number(id),
          operacao: e.operacao ? Number(e.operacao) : null,
          cst: e.cst || null,
          icmsCredito: e.icmsCredito || null,
          pisAliquota: e.pisAliquota ? Number(e.pisAliquota.replace(',', '.')) : null,
          cofinsAliquota: e.cofinsAliquota ? Number(e.cofinsAliquota.replace(',', '.')) : null,
          centro: e.centro ? Number(e.centro) : null,
        })),
        parcelas: Object.entries(parcelas).map(([id, e]) => ({
          id: Number(id),
          vencimento: e.vencimento || null,
          valor: e.valor ? Number(e.valor.replace(/\./g, '').replace(',', '.')) : null,
          classificacao: e.classificacao ? Number(e.classificacao) : null,
          formaPagamento: e.formaPagamento || null,
        })),
        novasParcelas: novas
          .filter((n) => n.vencimento && n.valor)
          .map((n) => ({ vencimento: n.vencimento, valor: Number(n.valor.replace(/\./g, '').replace(',', '.')) })),
        excluirParcelas: excluir,
        gerarParcelasOc: gerarOc,
        ignorarValidacao: ignorar,
      }),
    onSuccess: () => {
      limpar();
      void cliente.invalidateQueries({ queryKey: ['nf-compra', nfCompra] });
      void cliente.invalidateQueries({ queryKey: ['nf-compra-lista'] });
    },
  });

  const d = nf.data;
  if (nf.isLoading || !d) {
    return nf.error ? (
      <Cartao className="p-4 text-sm text-status-divergencia">{nf.error instanceof ErroHttp ? nf.error.message : String(nf.error)}</Cartao>
    ) : (
      <Cartao className="space-y-2 p-4">
        <Esqueleto className="h-8" />
        <Esqueleto className="h-40" />
      </Cartao>
    );
  }
  const travadoFiscal = !d.podeEditar || Boolean(d.travas.fiscal);
  const travadoFinanceiro = travadoFiscal || d.validada;
  const alterado = Boolean(oc || empresa || movimento || emissao || Object.keys(itens).length || Object.keys(parcelas).length || novas.length || excluir.length || gerarOc || ignorar);
  const numeroBr = (t: string): number => Number(t.replace(/\./g, '').replace(',', '.')) || 0;
  const somaParcelas =
    d.parcelas.filter((p) => !excluir.includes(p.id)).reduce((a, p) => a + (parcelas[p.id]?.valor ? numeroBr(parcelas[p.id]!.valor!) : p.valor), 0) +
    novas.reduce((a, n) => a + numeroBr(n.valor), 0);
  const falta = Math.round(((d.totalGeral ?? 0) - somaParcelas) * 100) / 100;
  const adicionarParcela = (): void => {
    const base = new Date(`${d.emissao ?? new Date().toISOString().slice(0, 10)}T12:00:00`);
    base.setDate(base.getDate() + 30 * (d.parcelas.length - excluir.length + novas.length + 1));
    definirNovas((x) => [...x, { vencimento: base.toISOString().slice(0, 10), valor: falta > 0 ? falta.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '' }]);
  };
  const mudarItem = (id: number, campo: keyof EdItem, valor: string): void =>
    definirItens((x) => ({ ...x, [id]: { ...x[id], [campo]: valor } }));
  const todos = (campo: keyof EdItem, valor: string): void =>
    definirItens((x) => Object.fromEntries(d.itens.map((i) => [i.id, { ...x[i.id], [campo]: valor }])));
  const mudarParcela = (id: number, campo: keyof EdParcela, valor: string): void =>
    definirParcelas((x) => ({ ...x, [id]: { ...x[id], [campo]: valor } }));

  return (
    <Cartao className="space-y-3 p-3">
      {/*
       * Cabeçalho compacto (Paulo, 01/11/2026: "pega muito espaço"): fornecedor com os selos e os botões
       * na mesma linha; os dados da NF numa linha só, com a chave; as consultas do PBS em chips.
       */}
      <div className="space-y-1.5 border-b border-borda pb-2.5">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <h2 className="truncate text-base font-bold leading-tight text-texto">{caixaAlta(d.fornecedor) ?? 'sem fornecedor'}</h2>
            <span
              className="inline-flex items-center gap-1 rounded-full bg-primaria-suave px-2 py-0.5 text-2xs font-semibold text-primaria"
              title={
                d.transferencia
                  ? `A empresa ${d.transferencia.empresaOrigem ?? '?'} mandou a mercadoria para a empresa ${d.empresa}: é a mesma NF da saída, lançada no destino.`
                  : d.remessa
                    ? 'NF de remessa (o par da venda à ordem): traz a mercadoria; o financeiro é da NF de venda. Sem OC e sem parcela é o normal.'
                    : d.transferida
                      ? `A compra do fornecedor; a mercadoria já saiu para ${d.transferida.destino ?? `a empresa ${d.transferida.empresa ?? '?'}`}.`
                      : 'A NF que o fornecedor emitiu: é ela que gera o título a pagar.'
              }
            >
              {d.transferencia ? <ArrowRightLeft className="h-3 w-3" /> : <ShoppingCart className="h-3 w-3" />}
              {d.transferencia ? 'Entrada de transferência' : d.remessa ? 'Remessa (sem financeiro)' : d.transferida ? 'Compra · já transferida' : 'Compra do fornecedor'}
            </span>
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-semibold',
                d.validada ? 'bg-status-livreFundo text-status-livre' : 'bg-status-atencaoFundo text-status-atencao',
              )}
              title={d.validada ? 'Salva no ERP: título a pagar e estoque gerados.' : 'Importada sem validação: ainda sem título e sem estoque.'}
            >
              {d.validada ? <CheckCircle2 className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
              {d.validada ? `Validada · ${d.titulos} título(s)${d.tituloPago ? ' · com pagamento' : ''}` : 'Pré-entrada'}
            </span>
            {d.chaveEsteira ? (
              <a className={cn(acaoTexto, 'text-2xs')} href={`/entradas?nota=${d.chaveEsteira}`}>
                Esteira <ArrowRight className="h-3 w-3" />
              </a>
            ) : null}
          </div>
          {/* Do acervo do portal fiscal: ver abre o PDF no navegador (imprimir); baixar salva. */}
          <div className="flex shrink-0 items-center gap-1">
            {[
              { href: `/api/entradas/nf-compra/${d.nfCompra}/arquivo/pdf`, nova: true, icone: Eye, rotulo: 'DANFE', titulo: 'Ver / imprimir a DANFE' },
              { href: `/api/entradas/nf-compra/${d.nfCompra}/arquivo/pdf?baixar=1`, nova: false, icone: Download, rotulo: 'Baixar', titulo: 'Baixar a DANFE' },
              { href: `/api/entradas/nf-compra/${d.nfCompra}/arquivo/xml?baixar=1`, nova: false, icone: FileCode2, rotulo: 'XML', titulo: 'Baixar o XML' },
            ].map((b) => (
              <a
                key={b.rotulo}
                href={b.href}
                title={b.titulo}
                {...(b.nova ? { target: '_blank', rel: 'noreferrer' } : {})}
                className="inline-flex h-7 items-center gap-1 rounded-md border border-borda bg-superficie px-2 text-2xs font-semibold text-texto transition hover:border-primaria hover:text-primaria"
              >
                <b.icone className="h-3.5 w-3.5" /> {b.rotulo}
              </a>
            ))}
            {/* Ação do mesmo documento fiscal: fica junto de DANFE e XML. */}
            {d.chave ? <ChaveNfe chave={d.chave.trim()} /> : null}
            <button
              type="button"
              onClick={aoExpandir}
              title={expandido ? 'Voltar à lista para consultar outra NF' : 'Usar a largura toda para editar'}
              className="hidden h-7 items-center gap-1 rounded-md border border-primaria/40 bg-primaria-suave px-2 text-2xs font-semibold text-primaria transition hover:border-primaria hover:bg-primaria hover:text-white lg:inline-flex"
            >
              {expandido ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              {expandido ? 'Voltar à lista' : 'Expandir'}
            </button>
          </div>
        </div>


        {/* Os auxiliares do formulário NF_COMPRA do PBS (Contábil, Fiscal, Espelho, Estoque…), lidos do dicionário. */}
        <AuxiliaresNfCompra nfCompra={d.nfCompra} />
      </div>

      {d.trilha ? <Trilha t={d.trilha} atual={d.nfCompra} /> : null}

      {d.transferencia && !d.trilha ? (
        <div className="flex gap-3 rounded-lg border border-borda border-l-4 border-l-primaria bg-superficie px-3 py-2.5 text-xs">
          <ArrowRightLeft className="mt-0.5 h-4 w-4 shrink-0 text-primaria" />
          <div className="min-w-0 space-y-0.5">
            <p className="text-[13px] font-semibold text-texto">
              Entrada de transferência: NF {d.numero} da empresa {d.transferencia.empresaOrigem ?? '?'} para a empresa {d.empresa}
            </p>
            <p className="text-textoFraco">
              {`Emitida pelo ${d.transferencia.peloWms ? 'WMS' : 'Procfit'}${d.transferencia.emitidaPor ? ` (${d.transferencia.emitidaPor})` : ''}`}
              {d.recebimento ? ` · cega ${d.recebimento} no destino` : ' · sem cega no destino ainda'}
            </p>
            {d.transferencia.compras.length ? (
              <p className="text-textoFraco">
                Compras de origem:{' '}
                {d.transferencia.compras.map((c, i) => (
                  <React.Fragment key={`${c.numero}-${i}`}>
                    {i ? ', ' : ''}
                    {c.nfCompra ? (
                      <a className="text-primaria underline" href={`/entradas?aba=nfcompra&nf=${c.nfCompra}`}>
                        NF {c.numero}
                      </a>
                    ) : (
                      `NF ${c.numero}`
                    )}
                  </React.Fragment>
                ))}
              </p>
            ) : (
              <p className="text-textoSuave">As compras de origem não estão na esteira do WMS.</p>
            )}
            <p className="text-textoSuave">Sem OC e sem parcela é o normal aqui: o título é da compra de origem.</p>
          </div>
        </div>
      ) : null}

      {d.transferida && !d.trilha ? (
        <div className="flex gap-3 rounded-lg border border-borda border-l-4 border-l-primaria bg-superficie px-3 py-2.5 text-xs">
          <ArrowRightLeft className="mt-0.5 h-4 w-4 shrink-0 text-primaria" />
          <div className="min-w-0 space-y-0.5">
            <p className="text-[13px] font-semibold text-texto">
              Transferida para {d.transferida.destino ?? `a empresa ${d.transferida.empresa ?? '?'}`}
              {d.transferida.destino && d.transferida.empresa ? ` (empresa ${d.transferida.empresa})` : ''}: NF de transferência {d.transferida.numero ?? '?'}
            </p>
            <p className="text-textoFraco">
              {`Emitida pelo ${d.transferida.peloWms ? 'WMS' : 'Procfit'}${d.transferida.emitidaPor ? ` (${d.transferida.emitidaPor})` : ''}`}
              {d.transferida.recebimento ? ` · cega ${d.transferida.recebimento} no destino` : ' · sem cega no destino ainda'}
            </p>
            <p>
              <a className="font-semibold text-primaria underline" href={`/entradas?aba=nfcompra&nf=${d.transferida.nfCompraDestino}`}>
                Abrir a entrada no destino (NF_COMPRA {d.transferida.nfCompraDestino})
              </a>
            </p>
          </div>
        </div>
      ) : null}

      {/* Trava do PBS (período fechado ou SQL-Restritiva do formulário): bloqueia a edição inteira, em destaque. */}
      {d.travas.fiscal ? (
        <p className="flex items-start gap-2 whitespace-pre-line rounded-lg border border-status-atencao/50 bg-status-atencaoFundo px-3 py-2 text-xs font-semibold text-status-atencao">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Esta NF não pode ser editada:{'\n'}
            <span className="font-normal">{d.travas.fiscal}</span>
          </span>
        </p>
      ) : null}
      {[d.travas.fiscal ? null : d.travas.financeiro, d.podeEditar ? null : 'Editar é do fiscal (entradas.fiscal).'].filter(Boolean).map((t) => (
        <p key={t} className="flex items-start gap-2 rounded-lg bg-superficie2 px-3 py-2 text-xs text-textoFraco">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {t}
        </p>
      ))}

      {/*
       * Dados da nota (Paulo, 01/11/2026): o que edita e o que só se lê no mesmo cartão. Editável = caixa branca
       * com borda; só leitura = pílula cinza com cadeado; alterado (ainda não salvo) = contorno âmbar.
       */}
      {(() => {
        const nomeEmpresa = (id: number): string | null => d.empresas?.find((e) => e.empresa === id)?.nome ?? null;
        const razaoSocial = (id: number): string | null => d.empresas?.find((e) => e.empresa === id)?.nomeCompleto ?? null;
        const empresaNova = empresa ? Number(empresa) : null;
        const trocaEmpresa = empresaNova != null && empresaNova !== d.empresa;
        const idEmpresa = trocaEmpresa ? empresaNova! : d.empresa;
        const nomeVisto = nomeEmpresa(idEmpresa);
        const razaoVista = razaoSocial(idEmpresa);
        const trocaOc = Boolean(oc) && Number(oc) !== d.pedidoCompra;
        const alterado = (sim: boolean): string => (sim ? 'border-status-atencao ring-2 ring-status-atencao/20' : '');
        const rotulo = 'block text-[10px] font-bold uppercase leading-4 tracking-wide text-textoSuave';
        return (
          <section className="overflow-hidden rounded-xl border border-borda bg-superficie shadow-xs">
            <header className="flex items-center justify-between gap-2 border-b border-borda bg-superficie2/60 px-4 py-2">
              <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-textoFraco">
                <FileText className="h-3.5 w-3.5" /> Dados da nota
              </h3>
              <span className="flex items-center gap-1 text-2xs text-textoSuave">
                <Lock className="h-3 w-3" /> só leitura
              </span>
            </header>

            {/* Empresa: a identidade da NF, numa faixa própria. */}
            <div className="border-b border-borda px-4 py-3">
              <div className="min-w-0">
                <label htmlFor="nfc-empresa" className={rotulo}>
                  Empresa
                </label>
                <div className="mt-1 flex min-w-0 items-center gap-2.5">
                  {/* Ícone e id num grupo só, na mesma altura (32px). */}
                  <span className="flex h-8 shrink-0">
                    <span className="grid h-8 w-8 place-items-center rounded-l-lg border border-r-0 border-borda bg-primaria-suave text-primaria">
                      <Building2 className="h-3.5 w-3.5" />
                    </span>
                    <Campo
                      id="nfc-empresa"
                      className={cn('h-8 !min-h-0 w-16 rounded-l-none px-2 text-center font-semibold', alterado(trocaEmpresa))}
                    inputMode="numeric"
                    placeholder={String(d.empresa)}
                    value={empresa}
                    onChange={(e) => definirEmpresa(e.target.value.replace(/[^0-9]/g, ''))}
                    disabled={travadoFinanceiro}
                      title={d.validada ? 'Validada: a empresa não muda por aqui (título e estoque já gerados).' : 'O id da empresa'}
                    />
                  </span>
                  <span className={cn('shrink-0 text-sm font-bold', trocaEmpresa ? (nomeVisto ? 'text-status-atencao' : 'text-status-divergencia') : 'text-texto')}>
                    {nomeVisto ?? (trocaEmpresa ? 'empresa fora do seu acesso' : '—')}
                  </span>
                  {razaoVista && razaoVista !== nomeVisto ? <span className="min-w-0 truncate text-xs text-textoSuave">{razaoVista}</span> : null}
                </div>
              </div>
            </div>

            {/* A grade: colunas iguais, tudo alinhado pela base. */}
            <div className="grid grid-cols-2 items-end gap-x-5 gap-y-3 px-4 py-3 sm:grid-cols-3 xl:grid-cols-6">
              <CampoLeitura rotulo="NF / série" valor={`${d.numero ?? '—'}/${d.serie ?? '—'}`} />
              <CampoLeitura rotulo="NF_COMPRA" valor={String(d.nfCompra)} />
              <div>
                <label htmlFor="nfc-oc" className={rotulo}>
                  Pedido (OC)
                </label>
                <Campo
                  id="nfc-oc"
                  className={cn('mt-1 h-8 !min-h-0 px-2.5', alterado(trocaOc))}
                  inputMode="numeric"
                  placeholder={String(d.pedidoCompra ?? '—')}
                  value={oc}
                  onChange={(e) => definirOc(e.target.value.replace(/[^0-9]/g, ''))}
                  disabled={travadoFiscal}
                />
              </div>
              <div>
                <label htmlFor="nfc-emissao" className={rotulo}>
                  Emissão
                </label>
                <Campo
                  id="nfc-emissao"
                  className={cn('mt-1 h-8 !min-h-0 px-2.5', alterado(Boolean(emissao) && emissao !== d.emissao))}
                  type="date"
                  value={emissao || d.emissao || ''}
                  onChange={(e) => definirEmissao(e.target.value)}
                  disabled={travadoFinanceiro}
                />
              </div>
              <div>
                <label htmlFor="nfc-movimento" className={rotulo}>
                  Movimento
                </label>
                <Campo
                  id="nfc-movimento"
                  className={cn('mt-1 h-8 !min-h-0 px-2.5', alterado(Boolean(movimento) && movimento !== d.movimento))}
                  type="date"
                  value={movimento || d.movimento || ''}
                  onChange={(e) => definirMovimento(e.target.value)}
                  disabled={travadoFinanceiro}
                />
              </div>
              <div className="text-right">
                <span className={rotulo}>Total da NF</span>
                <span className="tabular mt-1 flex h-8 items-center justify-end whitespace-nowrap text-base font-bold text-primaria">{reais(d.totalGeral)}</span>
              </div>
            </div>

            {trocaOc || trocaEmpresa ? (
              <div className="space-y-1 border-t border-status-atencao/30 bg-status-atencaoFundo/50 px-4 py-2 text-2xs text-status-atencao">
                {trocaOc ? (
                  <p className="flex items-start gap-1.5">
                    <AlertTriangle className="mt-px h-3 w-3 shrink-0" /> Ao salvar, a nota da esteira passa para a OC {oc} e é conferida de novo contra ela.
                  </p>
                ) : null}
                {trocaEmpresa ? (
                  <p className="flex items-start gap-1.5">
                    <AlertTriangle className="mt-px h-3 w-3 shrink-0" /> Os centros dos itens precisam ser da empresa {empresaNova}: troque-os abaixo, na mesma edição.
                  </p>
                ) : null}
              </div>
            ) : null}
          </section>
        );
      })()}

      {/*
       * IGNORAR_VALIDACAO do formulário: livre para marcar e desmarcar (Paulo, 01/11/2026); vai junto no Salvar.
       * Marcar não apaga título nem estoque já gerados. Desmarcar sem título valida (gera título e estoque).
       */}
      {(() => {
        const marcado = ignorar ? ignorar === 'S' : !d.validada;
        const mudou = ignorar != null;
        const aviso = !mudou
          ? null
          : ignorar === 'S'
            ? d.titulos
              ? `Ao salvar, a NF volta a ignorar a validação. Os ${d.titulos} título(s) a pagar e o estoque já gerados continuam como estão.`
              : 'Ao salvar, a NF volta a ignorar a validação.'
            : d.titulos
              ? 'Ao salvar, a NF deixa de ignorar a validação. O estoque é refeito pela integração do PBS; o título a pagar que já existe não é tocado.'
              : 'Ao salvar, a NF é validada no ERP: gera o título a pagar e o estoque, como salvar no Procfit.';
        return (
          <div className={cn('flex flex-wrap items-start gap-x-3 gap-y-1 rounded-lg border px-3 py-2 text-xs', mudou ? 'border-status-atencao/50 bg-status-atencaoFundo/40' : 'border-borda bg-superficie2/40')}>
            <label className={cn('inline-flex items-center gap-2 font-semibold text-texto', !travadoFiscal && 'cursor-pointer')}>
              <input
                type="checkbox"
                className="h-4 w-4 accent-primaria"
                checked={marcado}
                disabled={travadoFiscal}
                onChange={(e) => {
                  const v = e.target.checked ? 'S' : 'N';
                  definirIgnorar(v === (d.validada ? 'N' : 'S') ? null : v);
                }}
              />
              Ignorar validação
            </label>
            <span className="text-2xs text-textoSuave">
              {aviso ?? (d.validada ? `Desmarcado: título a pagar e estoque gerados (${d.titulos} título(s)).` : 'Marcado: pré-entrada, sem título e sem estoque.')}
            </span>
          </div>
        );
      })()}

      <div>
        <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-bold text-texto">Itens</h3>
          {!travadoFiscal ? (
            <div className="flex flex-wrap items-center gap-1.5 text-2xs text-textoFraco">
              Em todos:
              <select className="rounded border border-borda bg-superficie px-1 py-0.5" defaultValue="" onChange={(e) => e.target.value && todos('operacao', e.target.value)}>
                <option value="">operação…</option>
                {(ops.data?.operacoes ?? []).map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.id} · {o.descricao.slice(0, 50)}
                  </option>
                ))}
              </select>
              <select className="rounded border border-borda bg-superficie px-1 py-0.5" defaultValue="" onChange={(e) => e.target.value && todos('icmsCredito', e.target.value)}>
                <option value="">crédito ICMS…</option>
                <option value="S">S</option>
                <option value="N">N</option>
              </select>
              <input className="w-16 rounded border border-borda bg-superficie px-1 py-0.5" placeholder="PIS %" onBlur={(e) => e.target.value && todos('pisAliquota', e.target.value)} />
              <input className="w-20 rounded border border-borda bg-superficie px-1 py-0.5" placeholder="COFINS %" onBlur={(e) => e.target.value && todos('cofinsAliquota', e.target.value)} />
            </div>
          ) : null}
        </div>
        <div className="overflow-x-auto rounded-lg border border-borda">
          <table className="w-full text-xs">
            <thead className="bg-superficie2 text-2xs uppercase text-textoSuave">
              <tr>
                <th className="px-2 py-1.5 text-left">Produto</th>
                <th className="px-2 py-1.5 text-right">Qtd</th>
                <th className="px-2 py-1.5 text-right">Total</th>
                <th className="px-2 py-1.5 text-left">Operação</th>
                <th className="px-2 py-1.5 text-left">CST</th>
                <th className="px-2 py-1.5 text-left">Créd. ICMS</th>
                <th className="px-2 py-1.5 text-right">PIS %</th>
                <th className="px-2 py-1.5 text-right">COFINS %</th>
                <th className="px-2 py-1.5 text-left">Centro</th>
              </tr>
            </thead>
            <tbody>
              {d.itens.map((i) => {
                const e = itens[i.id] ?? {};
                const cel = 'w-full rounded border border-borda bg-superficie px-1 py-0.5 disabled:border-transparent disabled:bg-transparent';
                const mudou = (campo: keyof EdItem): string => (e[campo] ? 'border-primaria bg-primaria-suave/40' : '');
                return (
                  <tr key={i.id} className="border-t border-borda align-top">
                    <td className="px-2 py-1.5">
                      <span className="tabular font-mono font-semibold">{i.produto}</span> <span className="text-textoFraco">{i.descricao}</span>
                    </td>
                    <td className="tabular px-2 py-1.5 text-right">{i.quantidade}</td>
                    <td className="tabular px-2 py-1.5 text-right">{reais(i.total)}</td>
                    <td className="px-2 py-1.5">
                      <select
                        className={cn(cel, 'min-w-[150px]', mudou('operacao'))}
                        value={e.operacao ?? String(i.operacao ?? '')}
                        disabled={travadoFiscal}
                        onChange={(x) => mudarItem(i.id, 'operacao', x.target.value)}
                        title={i.operacaoDescricao ?? undefined}
                      >
                        {i.operacao != null && !(ops.data?.operacoes ?? []).some((o) => o.id === i.operacao) ? (
                          <option value={i.operacao}>{i.operacao} · {i.operacaoDescricao}</option>
                        ) : null}
                        {(ops.data?.operacoes ?? []).map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.id} · {o.descricao.slice(0, 40)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-1.5">
                      <input className={cn(cel, 'w-14', mudou('cst'))} value={e.cst ?? i.cst ?? ''} disabled={travadoFiscal} onChange={(x) => mudarItem(i.id, 'cst', x.target.value.replace(/\D/g, '').slice(0, 3))} />
                    </td>
                    <td className="px-2 py-1.5">
                      <select className={cn(cel, 'w-14', mudou('icmsCredito'))} value={e.icmsCredito ?? i.icmsCredito ?? 'N'} disabled={travadoFiscal} onChange={(x) => mudarItem(i.id, 'icmsCredito', x.target.value)}>
                        <option value="S">S</option>
                        <option value="N">N</option>
                      </select>
                      <span className="text-2xs text-textoSuave">ICMS {i.icmsAliquota ?? 0}%</span>
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <input className={cn(cel, 'w-16 text-right', mudou('pisAliquota'))} value={e.pisAliquota ?? String(i.pisAliquota ?? '')} disabled={travadoFiscal} onChange={(x) => mudarItem(i.id, 'pisAliquota', x.target.value)} />
                      <span className="tabular text-2xs text-textoSuave">{reais(i.pisValor)}</span>
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <input className={cn(cel, 'w-16 text-right', mudou('cofinsAliquota'))} value={e.cofinsAliquota ?? String(i.cofinsAliquota ?? '')} disabled={travadoFiscal} onChange={(x) => mudarItem(i.id, 'cofinsAliquota', x.target.value)} />
                      <span className="tabular text-2xs text-textoSuave">{reais(i.cofinsValor)}</span>
                    </td>
                    <td className="px-2 py-1.5">
                      <input className={cn(cel, 'w-16', mudou('centro'))} value={e.centro ?? String(i.centro ?? '')} disabled={travadoFinanceiro} onChange={(x) => mudarItem(i.id, 'centro', x.target.value.replace(/\D/g, ''))} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-1 text-2xs text-textoSuave">
          Total PIS {reais(d.totalPis)} · COFINS {reais(d.totalCofins)}. Valor de PIS/COFINS é recalculado sobre a base ao salvar.
        </p>
      </div>

      <div>
        <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-bold text-texto">Parcelas</h3>
          {!travadoFinanceiro ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {d.pedidoCompra && d.parcelas.length - excluir.length === 0 && !novas.length ? (
                <Botao variante={gerarOc ? 'primaria' : 'secundaria'} tamanho="pequeno" onClick={() => definirGerarOc((x) => !x)}>
                  <Wand2 className="h-4 w-4" /> {gerarOc ? 'Vai gerar pela OC ao salvar' : `Gerar pela OC ${d.pedidoCompra}`}
                </Botao>
              ) : null}
              <Botao variante="secundaria" tamanho="pequeno" onClick={adicionarParcela} disabled={gerarOc}>
                <Plus className="h-4 w-4" /> Adicionar parcela
              </Botao>
            </div>
          ) : null}
        </div>
        {d.parcelas.length || novas.length ? (
          <div className="overflow-x-auto rounded-lg border border-borda">
            <table className="w-full text-xs">
              <thead className="bg-superficie2 text-2xs uppercase text-textoSuave">
                <tr>
                  <th className="px-2 py-1.5 text-left">Título</th>
                  <th className="px-2 py-1.5 text-left">Vencimento</th>
                  <th className="px-2 py-1.5 text-right">Valor</th>
                  <th className="px-2 py-1.5 text-left">Classificação</th>
                  <th className="px-2 py-1.5 text-left">Forma de pagamento</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {d.parcelas.map((p) => {
                  const e = parcelas[p.id] ?? {};
                  const cel = 'rounded border border-borda bg-superficie px-1 py-0.5 disabled:border-transparent disabled:bg-transparent';
                  if (excluir.includes(p.id)) {
                    return (
                      <tr key={p.id} className="border-t border-borda text-textoSuave line-through">
                        <td className="px-2 py-1.5" colSpan={5}>
                          {p.titulo} · {dia(p.vencimento)} · {reais(p.valor)} — será excluída
                        </td>
                        <td className="px-2 py-1.5">
                          <button type="button" title="Manter" onClick={() => definirExcluir((x) => x.filter((i) => i !== p.id))}>
                            <Undo2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={p.id} className="border-t border-borda">
                      <td className="tabular px-2 py-1.5">{p.titulo}</td>
                      <td className="px-2 py-1.5">
                        <input type="date" className={cel} value={e.vencimento ?? p.vencimento ?? ''} disabled={travadoFinanceiro} onChange={(x) => mudarParcela(p.id, 'vencimento', x.target.value)} />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <input className={cn(cel, 'w-28 text-right')} value={e.valor ?? p.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} disabled={travadoFinanceiro} onChange={(x) => mudarParcela(p.id, 'valor', x.target.value)} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input className={cn(cel, 'w-16')} value={e.classificacao ?? String(p.classificacao ?? '')} disabled={travadoFinanceiro} onChange={(x) => mudarParcela(p.id, 'classificacao', x.target.value.replace(/\D/g, ''))} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input className={cn(cel, 'w-12')} value={e.formaPagamento ?? p.formaPagamento ?? ''} disabled={travadoFinanceiro} onChange={(x) => mudarParcela(p.id, 'formaPagamento', x.target.value.replace(/\D/g, '').slice(0, 2))} />
                      </td>
                      <td className="px-2 py-1.5">
                        {!travadoFinanceiro ? (
                          <button type="button" title="Excluir parcela" className="text-status-divergencia" onClick={() => definirExcluir((x) => [...x, p.id])}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
                {novas.map((n, k) => (
                  <tr key={`nova-${k}`} className="border-t border-borda bg-primaria-suave/30">
                    <td className="px-2 py-1.5 text-primaria">nova</td>
                    <td className="px-2 py-1.5">
                      <input type="date" className="rounded border border-primaria bg-superficie px-1 py-0.5" value={n.vencimento} onChange={(x) => definirNovas((v) => v.map((y, i) => (i === k ? { ...y, vencimento: x.target.value } : y)))} />
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <input className="w-28 rounded border border-primaria bg-superficie px-1 py-0.5 text-right" value={n.valor} placeholder="0,00" onChange={(x) => definirNovas((v) => v.map((y, i) => (i === k ? { ...y, valor: x.target.value } : y)))} />
                    </td>
                    <td className="px-2 py-1.5 text-textoSuave">1</td>
                    <td className="px-2 py-1.5 text-textoSuave">15</td>
                    <td className="px-2 py-1.5">
                      <button type="button" title="Tirar" onClick={() => definirNovas((v) => v.filter((_, i) => i !== k))}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="flex items-center gap-1.5 text-xs text-status-divergencia">
            <AlertTriangle className="h-3.5 w-3.5" />
            {gerarOc
              ? 'As parcelas saem da condição da OC ao salvar.'
              : d.remessa || d.transferencia
                ? 'Sem parcela: o normal aqui (o título é da NF de venda ou da compra de origem).'
                : 'Sem parcela: gere pela OC ou adicione à mão.'}
          </p>
        )}
        {(d.parcelas.length || novas.length) && !travadoFinanceiro ? (
          <p className={cn('mt-1 text-2xs', Math.abs(falta) > 0.05 ? 'font-semibold text-status-atencao' : 'text-textoSuave')}>
            Parcelas {reais(somaParcelas)} de {reais(d.totalGeral)}
            {Math.abs(falta) > 0.05 ? ` · ${falta > 0 ? 'faltam' : 'sobram'} ${reais(Math.abs(falta))} — o Salvar só aceita com a soma igual ao total` : ' · fecha com o total'}
          </p>
        ) : null}
      </div>

      {salvar.error ? (
        <p className="rounded-lg bg-status-divergenciaFundo px-3 py-2 text-xs text-status-divergencia">
          {salvar.error instanceof ErroHttp ? salvar.error.message : String(salvar.error)}
        </p>
      ) : null}
      {salvar.data ? (
        <p className="rounded-lg bg-status-livreFundo px-3 py-2 text-xs text-status-livre">
          {salvar.data.mudancas.length ? `Salvo como no Procfit: ${salvar.data.mudancas.join('; ')}.` : 'Nada mudou.'}
        </p>
      ) : null}
      {d.podeEditar ? (
        <div className="flex items-center justify-end gap-2 border-t border-borda pt-3">
          <Botao variante="fantasma" tamanho="pequeno" onClick={limpar} disabled={!alterado || salvar.isPending}>
            <Undo2 className="h-4 w-4" /> Desfazer
          </Botao>
          <Botao variante="primaria" tamanho="pequeno" onClick={() => salvar.mutate()} carregando={salvar.isPending} disabled={!alterado || travadoFiscal}>
            <Save className="h-4 w-4" /> Salvar (validação e gravação final do ERP)
          </Botao>
        </div>
      ) : null}
    </Cartao>
  );
}

/**
 * Altura da lista da esquerda até o pé da tela, recalculada ao rolar: grudada
 * no alto (página rolada para editar a nota) ela ocupa a tela toda. A altura
 * fixa (100vh − 258px) servia só com a página no topo e, rolada, parava no meio.
 */
function useAlturaAteORodape(): { ref: React.RefObject<HTMLDivElement>; estilo: React.CSSProperties | undefined } {
  const ref = React.useRef<HTMLDivElement>(null);
  const [altura, definirAltura] = React.useState<number | null>(null);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const medir = (): void => {
      // Só no layout de duas colunas (lg): no celular a lista corre com a página.
      if (!window.matchMedia('(min-width: 1024px)').matches) return definirAltura(null);
      definirAltura(Math.max(360, Math.floor(window.innerHeight - el.getBoundingClientRect().top - 12)));
    };
    medir();
    // Captura: a rolagem pode ser de um contêiner interno, não da janela.
    window.addEventListener('scroll', medir, { capture: true, passive: true });
    window.addEventListener('resize', medir);
    return () => {
      window.removeEventListener('scroll', medir, { capture: true });
      window.removeEventListener('resize', medir);
    };
  }, []);
  return { ref, estilo: altura == null ? undefined : { height: altura } };
}
